import PropTypes from 'prop-types'
import React from 'react'
import { View } from 'components/View'
import {
  logUtil,
  throttleUtil
} from 'packages/src'

/**
 * Delay used to throttle the vertical scroll.
 * @type {number}
 */
const SCROLL_THROTTLE_IN_MS = 300

const RATIO_REMAINING_THRESHOLD_BEFORE_REACHING_END = 1.5

class Scroll extends React.Component {
  static defaultProps = {
    direction: View.DIRECTION.COLUMN,
    isHorizontal: false,
    isBoundToWindow: true,
    onScroll: null
  }

  static propTypes = {
    children: PropTypes.arrayOf(
      PropTypes.element
    ).isRequired,
    /**
     * The current pathname.
     * Used to re-init the variables when changing location.
     * @type {string}
     */
    pathname: PropTypes.string,

    direction: PropTypes.string,
    /**
     * Whether or not the scroll position should be compared to the window
     * or the parent element.
     * @type {boolean}
     */
    isBoundToWindow: PropTypes.bool,
    shouldNotWrap: PropTypes.bool,
    isHorizontal: PropTypes.bool,
    /**
     * Callback triggered when the scroll position reached the bottom
     * of the current element.
     * @type {Function}
     */
    onReachedEnd: PropTypes.func.isRequired,
    onScroll: PropTypes.func
  }

  _lastScrollPosition = 0

  constructor (props) {
    super(props)
    this.handleScroll = throttleUtil(
      this.handleScroll.bind(this),
      SCROLL_THROTTLE_IN_MS
    )
    this._ref = React.createRef()
    this._lastScrollPosition = null
  }

  componentDidMount () {
    const isBoundToWindow = this.props.isBoundToWindow
    const scrollableElement = isBoundToWindow
      ? window
      : this._ref.current
    scrollableElement.addEventListener('scroll', this.handleScroll)
  }

  componentDidUpdate (prevProps, prevState, snapshot) {
    if (
      prevProps.pathname !== this.props.pathname &&
      this.props.onReachedEnd
    ) {
      this.init()
    }
  }

  componentWillUnmount () {
    const isBoundToWindow = this.props.isBoundToWindow
    const scrollableElement = isBoundToWindow
      ? window
      : this._ref.current
    scrollableElement.removeEventListener('scroll', this.handleScroll)
  }

  /**
   * The function called when the application is being scrolled.
   */
  handleScroll () {
    const { onScroll } = this.props
    if (onScroll) {
      onScroll()
    }
    this.handleReachedEnd()
  }

  handleReachedEnd () {
    const scrollableElement = this.props.isBoundToWindow
      ? document.documentElement
      : this._ref.current

    const { isHorizontal } = this.props
    const xOrYKey = isHorizontal ? 'X' : 'Y'
    const leftOrTopKey = isHorizontal ? 'Left' : 'Top'
    const widthOrHeightKey = isHorizontal ? 'Width' : 'Height'

    let scrollPosition
    if (!this.props.isBoundToWindow) {
      scrollPosition = scrollableElement[`scroll${leftOrTopKey}`]
    } else {
      scrollPosition = (
        window[`page${xOrYKey}Offset`] ||
        scrollableElement[`scroll${leftOrTopKey}`]
      )
    }

    const scrollLengthInPixels = scrollableElement[`scroll${widthOrHeightKey}`]
    const lengthInPixels = scrollableElement[`offset${widthOrHeightKey}`]
    const pixelsRemaining = scrollLengthInPixels - scrollPosition - lengthInPixels
    const ratioRemaining = pixelsRemaining / lengthInPixels

    const debugProps = {
      ratioRemaining,
      RATIO_REMAINING_THRESHOLD_BEFORE_REACHING_END
    }
    logUtil('Scroll#handleReachedEnd').debug('debugProps=', debugProps)
    if (
      ratioRemaining < RATIO_REMAINING_THRESHOLD_BEFORE_REACHING_END &&
      (
        !this._lastScrollPosition ||
        scrollPosition > this._lastScrollPosition
      )
    ) {
      this._lastScrollPosition = scrollPosition + pixelsRemaining
      const onReachedEnd = this.props.onReachedEnd
      if (onReachedEnd) {
        onReachedEnd()
      }
    }
  }

  /**
   * Init the infinite scroll properties.
   */
  init () {
    this._lastScrollPosition = 0
  }

  render () {
    return (
      <div
        ref={this._ref}
        style={{
          display: 'flex',
          flexDirection: (
            this.props.direction ||
            View.DIRECTION.COLUMN
          ),
          flexWrap: (this.props.direction === View.DIRECTION.ROW && !this.props.shouldNotWrap) ? 'wrap' : null,
          padding: '0',
          overflowX: this.props.direction === View.DIRECTION.ROW ? 'scroll' : null,
          overflowY: this.props.direction !== View.DIRECTION.ROW ? 'scroll' : null,
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {this.props.children}
      </div>
    )
  }
}

export default Scroll
